<script setup lang="ts">
import '@hprofits/ui/style.css';
import '@hprofits/ui-smart-controls/style.css';

import type { ResizableOptions } from '@hprofits/ui';
import { PageShell, PageShellContent, PageShellPanel, useBreakpoints, vResizable } from '@hprofits/ui';
import { computed, ref, watchEffect } from 'vue';

import { CampaignGroupParams } from '@/modules/campaigns/composables/context/types';
import { useCampaignGroupContextProvider } from '@/modules/campaigns/composables/context/useCampaignGroupContextProvider';

import CampaignGroupHeader from './CampaignGroupHeader.vue';
import CampaignGroupSidebar from './CampaignGroupSidebar.vue';

const props = defineProps<CampaignGroupParams>();

const { rightPanel } = useCampaignGroupContextProvider(props);
const {
  isAvailable: isRightPanelAvailable,
  isOpen: isRightPanelOpen,
  setDesktopAvailable: setRightPanelDesktopAvailable,
} = rightPanel;

const { down } = useBreakpoints();
const isCompactLayout = computed(() => down('lg'));
const isMobileMenuOpen = ref(false);

const RIGHT_PANEL_WIDTH_STORAGE_KEY = 'campaigns:right-panel-width';
const RIGHT_PANEL_DEFAULT_WIDTH = 360;
const RIGHT_PANEL_MIN_WIDTH = 280;
const RIGHT_PANEL_MAX_WIDTH = 640;

const normalizeRightPanelWidth = (width: number): number => {
  if (!Number.isFinite(width)) {
    return RIGHT_PANEL_DEFAULT_WIDTH;
  }
  return Math.min(RIGHT_PANEL_MAX_WIDTH, Math.max(RIGHT_PANEL_MIN_WIDTH, width));
};

const readRightPanelWidth = (): number => {
  try {
    const storedWidth = globalThis.localStorage?.getItem(RIGHT_PANEL_WIDTH_STORAGE_KEY);
    if (storedWidth === null || storedWidth === undefined) {
      return RIGHT_PANEL_DEFAULT_WIDTH;
    }
    return normalizeRightPanelWidth(Number(storedWidth));
  } catch {
    return RIGHT_PANEL_DEFAULT_WIDTH;
  }
};

const rightPanelWidth = ref(readRightPanelWidth());
const rightPanelResizeOptions: ResizableOptions = {
  edge: 'left',
  min: RIGHT_PANEL_MIN_WIDTH,
  max: RIGHT_PANEL_MAX_WIDTH,
  onResize: (width) => {
    rightPanelWidth.value = normalizeRightPanelWidth(width);
    try {
      globalThis.localStorage?.setItem(RIGHT_PANEL_WIDTH_STORAGE_KEY, String(rightPanelWidth.value));
    } catch {
      // The panel remains resizable when storage is unavailable.
    }
  },
};

watchEffect(() => {
  setRightPanelDesktopAvailable(!isCompactLayout.value);
});

const setMobileMenuOpen = (): void => {
  isMobileMenuOpen.value = true;
};
</script>

<template>
  <PageShell class="hp-ui-boundary">
    <CampaignGroupHeader :set-mobile-menu-open="setMobileMenuOpen" />
    <CampaignGroupSidebar :is-compact-layout="isCompactLayout" v-model:is-mobile-menu-open="isMobileMenuOpen" />
    <PageShellContent>
      <slot />
    </PageShellContent>
    <PageShellPanel
      v-show="isRightPanelAvailable && isRightPanelOpen"
      v-resizable="rightPanelResizeOptions"
      class="campaign-right-panel"
      :style="{ width: `${rightPanelWidth}px` }"
    >
      <div id="campaign-right-panel-target" class="campaign-right-panel__target" />
    </PageShellPanel>
  </PageShell>
</template>

<style lang="scss">
body {
  background: var(--background-base) !important;
  overflow: hidden;
}

.campaign-right-panel {
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden auto;
  background: var(--background-base);
  border-left: 1px solid var(--surface-border);
}

.campaign-right-panel__target {
  min-height: 100%;
}
</style>
